import React, { useEffect, useState } from "react";
import styles from "./SigningUpgrades.module.scss";
import classNames from "classnames/bind";
import { useAppDispatch } from "~/redux/hooks";
import FormInput from "~/components/common/form/formInput/FormInput";
import BaseSelectCustom from "~/components/common/base-select-custom/BaseSelectCustom";
import { DragIcon } from "~/assets/icons";
import AddItem from "~/components/common/add-item/AddItem";
import DroppableArea from "~/components/common/drop-drag/DroppableArea";
import BaseBoxContent from "~/components/common/base-box-content/BaseBoxContent";
import FormToggle from "~/components/common/form/formToggle/FormToggle";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import { EstimateUoMOption } from "~/pages/admin/jobs/add-job/constants/add-estimate.constants";
import type { SigningUpgradesFormData } from "~/utils/interface/estimateSetting";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { handleCreateSigningUpgrades } from "~/thunks/estimate-setting/estimateSettingThunk";
import { estimateSettingActions } from "~/thunks/estimate-setting/estimateSettingSlice";
import { notify } from "~/utils/helper";

const cx = classNames.bind(styles);

const DEFAULT_ITEM: SigningUpgradesFormData["items"][number] = {
    itemName: "",
    itemDescription: "",
    type: "MATERIAL" as const,
    uom: "",
    quantity: 0,
    unitPrice: 0,
    sortOrder: 1,
};

const buildDefaultSelections = (): SigningUpgradesFormData["selections"] => [
    { fieldName: "", customerInput: "", sortOrder: 1 },
    { fieldName: "", customerInput: "", sortOrder: 2 },
    { fieldName: "", customerInput: "", sortOrder: 3 },
];

interface SigningUpgradesProps {
    templateName?: string;
    templateType?: string;
    location?: string;
    onCancel?: () => void;
}

const SigningUpgrades = (props: SigningUpgradesProps) => {
    const { templateName: nameFromParent, templateType, onCancel } = props;
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState<SigningUpgradesFormData>({
        templateName: nameFromParent ?? "",
        type: templateType ?? "",
        disclaimer: "",
        isDeposit: false,
        optionalTitle: "Sections",
        selectionsTitle: "My Product Selections",
        footer: "",
        selections: buildDefaultSelections(),
        items: [{ ...DEFAULT_ITEM }],
    });

    useEffect(() => {
        if (nameFromParent != null && nameFromParent !== "") {
            setFormData((prev) => ({ ...prev, templateName: nameFromParent }));
        }
    }, [nameFromParent]);

    useEffect(() => {
        if (templateType != null && templateType !== "") {
            setFormData((prev) => ({ ...prev, type: templateType }));
        }
    }, [templateType]);

    const handleUpdateFormData = (update: Partial<SigningUpgradesFormData>) => {
        setFormData((prev) => ({ ...prev, ...update }));
    };

    const handleItemUpdate = (update: Partial<SigningUpgradesFormData["items"][number]>, indexItem: number) => {
        setFormData((prev) => {
            const items = [...(prev.items ?? [])];
            items[indexItem] = { ...items[indexItem], ...update };
            return { ...prev, items };
        });
    };

    const handleAddItem = () => {
        setFormData((prev) => {
            const items = [...(prev.items ?? [])];
            const nextSortOrder = items.length + 1;
            items.push({ ...DEFAULT_ITEM, sortOrder: nextSortOrder });
            return { ...prev, items };
        });
    };

    const handleRemoveItem = (indexItem: number) => {
        setFormData((prev) => {
            const items = [...(prev.items ?? [])];
            items.splice(indexItem, 1);
            const normalized = (items.length ? items : [{ ...DEFAULT_ITEM }]).map((it, idx) => ({
                ...it,
                sortOrder: idx + 1,
            }));
            return { ...prev, items: normalized };
        });
    };

    const handleDropItems = (newItems: SigningUpgradesFormData["items"]) => {
        const normalized = (newItems ?? []).map((it, idx) => ({ ...it, sortOrder: idx + 1 }));
        handleUpdateFormData({ items: normalized.length ? normalized : [{ ...DEFAULT_ITEM }] });
    };

    const handleUpdateSelectionItem = (
        update: Partial<SigningUpgradesFormData["selections"][number]>,
        index: number
    ) => {
        setFormData((prev) => {
            const selections = [...(prev.selections ?? buildDefaultSelections())];
            selections[index] = { ...selections[index], ...update, sortOrder: selections[index]?.sortOrder ?? index + 1 };
            return { ...prev, selections };
        });
    };

    const handleDropSelections = (newSelections: SigningUpgradesFormData["selections"]) => {
        const normalized = (newSelections ?? []).map((s, idx) => ({ ...s, sortOrder: idx + 1 }));
        // enforce 3 selections
        const fixed = normalized.slice(0, 3);
        while (fixed.length < 3) fixed.push({ fieldName: "", customerInput: "", sortOrder: fixed.length + 1 });
        handleUpdateFormData({ selections: fixed });
    };

    const totalItems = (items: SigningUpgradesFormData["items"] = []) =>
        items.reduce((sum, s) => sum + (Number(s.quantity) || 0) * (Number(s.unitPrice) || 0), 0).toFixed(2);

    const handleAddSigningUpgrades = () => {
        const normalizedSelections = (formData.selections ?? buildDefaultSelections())
            .map((s, idx) => ({
                fieldName: s.fieldName ?? "",
                customerInput: s.customerInput ?? "",
                sortOrder: idx + 1,
            }))
            .slice(0, 3);
        while (normalizedSelections.length < 3) {
            normalizedSelections.push({
                fieldName: "",
                customerInput: "",
                sortOrder: normalizedSelections.length + 1,
            });
        }

        const normalizedItemsDraft = (formData.items ?? [])
            .map((it, idx) => ({
                ...it,
                itemName: it.itemName ?? "",
                itemDescription: it.itemName ?? "",
                uom: it.uom ?? "",
                quantity: Number(it.quantity) || 0,
                unitPrice: Number(it.unitPrice) || 0,
                sortOrder: idx + 1,
            }))
            .map((it) => ({
                ...it,
                itemName: String(it.itemName ?? "").trim(),
                uom: String(it.uom ?? "").trim(),
            }));

        const activeItems = normalizedItemsDraft.filter(
            (it) => it.itemName !== "" || it.uom !== "" || it.quantity !== 0 || it.unitPrice !== 0
        );

        if (activeItems.length === 0) {
            notify("error", "Item Name and UoM are required");
            return;
        }

        const invalidIndex = activeItems.findIndex((it) => it.itemName === "" || it.uom === "");
        if (invalidIndex !== -1) {
            const rowNumber = invalidIndex + 1;
            if (activeItems[invalidIndex].itemName === "" && activeItems[invalidIndex].uom === "") {
                notify("error", `Line ${rowNumber}: Item Name and UoM are required`);
            } else if (activeItems[invalidIndex].itemName === "") {
                notify("error", `Line ${rowNumber}: Item Name is required`);
            } else {
                notify("error", `Line ${rowNumber}: UoM is required`);
            }
            return;
        }

        setLoading(true);

        const payload: SigningUpgradesFormData = {
            templateName: formData.templateName ?? "",
            type: templateType ?? formData.type ?? "",
            disclaimer: formData.disclaimer ?? "",
            isDeposit: Boolean(formData.isDeposit),
            optionalTitle: formData.optionalTitle ?? "",
            selectionsTitle: formData.selectionsTitle ?? "",
            footer: formData.footer ?? "",
            selections: normalizedSelections,
            items: activeItems,
        };

        dispatch(handleCreateSigningUpgrades(payload))
            .unwrap()
            .then(() => {
                dispatch(estimateSettingActions.changeNeedRefreshDataEstimateTemplates(true));
                onCancel?.();
                notify("success", "Create signing & upgrades template successfully");
            })
            .catch((error: any) => {
                notify("error", error?.response?.data?.message || "Create signing & upgrades template failed");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const items = formData.items ?? [];
    const selections = formData.selections ?? buildDefaultSelections();

    return (
        <div className={cx("signing-upgrades-container")}>
            <div className={cx("template-name-block")}>
                <FormInput
                    name="Template Name"
                    text="Template Name"
                    isViewMode={false}
                    placeholder="Template Name"
                    inputkey="templateName"
                    value={formData.templateName ?? ""}
                    height="56px"
                    onChange={(e) => handleUpdateFormData({ templateName: e.target.value })}
                />
            </div>

            <div className={cx("template-info")}>
                <h3 className={cx("template-info-title")}>Signing Upgrades                </h3>
                <div className={cx("button-container")}>
                    <OutlinedButton
                        title="Cancel"
                        onClick={() => onCancel?.()}
                        className={cx("cancel-button")}
                    />

                    <PrimaryButton
                        title="Add Signing Upgrades"
                        onClick={handleAddSigningUpgrades}
                        className={cx("add-introduction-button")}
                        disabled={loading}
                    />
                </div>
            </div>

            <div className={cx("content")}>
                {/* Section Disclaimer */}
                <div className={cx("disclaimer-block")}>
                    <FormInput
                        name="Disclaimer"
                        text="Disclaimer"
                        isViewMode={false}
                        placeholder="Disclaimer"
                        inputkey="disclaimer"
                        value={formData.disclaimer ?? ""}
                        height="56px"
                        onChange={(e) => handleUpdateFormData({ disclaimer: e.target.value })}
                    />
                </div>

                {/* Sections: Item, UoM, Quantity, Price, Line Total */}
                <BaseBoxContent className={cx("section-item")} expanded>
                    <div data-slot="header">
                        <div className={cx("section-title")}>
                            <div className={cx("drag-button")}>
                                <DragIcon className={cx("drag-icon")} />
                            </div>
                            <TitleEditor
                                value={formData.optionalTitle ?? "Sections"}
                                name=""
                                placeholder=""
                                inputkey=""
                                isViewMode={false}
                                onChange={(value) =>
                                    handleUpdateFormData({ optionalTitle: value })
                                }
                                styleView={{ fontSize: "16px", fontWeight: 500 }}
                            />
                        </div>
                    </div>
                    <div data-slot="content">
                        <div className={cx("section-item-table")}>
                            <div className={cx("section-item-header")}>
                                <div className="w-[24px]" />
                                <div className="w-[28%]">
                                    <h3>Item</h3>
                                </div>
                                <div className="w-[16%]">
                                    <h3>UoM</h3>
                                </div>
                                <div className="w-[16%]">
                                    <h3>Quantity</h3>
                                </div>
                                <div className="w-[16%]">
                                    <h3>Price</h3>
                                </div>
                                <div className="w-[19%]">
                                    <h3>Line Total</h3>
                                </div>
                                <div className="w-[5%]" />
                            </div>
                            <div className={cx("section-item-table-body")}>
                                <DroppableArea
                                    data={items}
                                    groupName="step5-sections"
                                    onChange={handleDropItems}
                                    renderComponent={(
                                        item: SigningUpgradesFormData["items"][number],
                                        indexItem: number
                                    ) => (
                                        <div className={cx("section-item-table-row")}>
                                            <div className={cx("drag-button")}>
                                                <DragIcon className={cx("drag-icon")} />
                                            </div>
                                            <div className="w-[28%]">
                                                <FormInput
                                                    name="itemName"
                                                    text=""
                                                    isViewMode={false}
                                                    isViewNoInput
                                                    placeholder="Item"
                                                    inputkey="itemName"
                                                    value={item.itemName ?? ""}
                                                    onChange={(e) =>
                                                        handleItemUpdate({ itemName: e.target.value }, indexItem)
                                                    }
                                                    height="31px"
                                                />
                                            </div>
                                            <div className="w-[16%]">
                                                <BaseSelectCustom
                                                    selectedView={item.uom ?? ""}
                                                    onViewChange={(viewId) =>
                                                        handleItemUpdate(
                                                            { uom: viewId != null ? String(viewId) : "" },
                                                            indexItem
                                                        )
                                                    }
                                                    height="36px"
                                                    isViewMode={false}
                                                    isViewNoInput
                                                    viewOptions={EstimateUoMOption}
                                                    placeholder="UoM"
                                                    className={cx("select-filter")}
                                                    minWidth="100px"
                                                    textAlign="left"
                                                    IconDrop={
                                                        <div className={cx("drop")}>
                                                            <svg
                                                                width="24"
                                                                height="24"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    d="M11.9994 14.308L8.19141 10.5H15.8074L11.9994 14.308Z"
                                                                    fill="black"
                                                                />
                                                            </svg>
                                                        </div>
                                                    }
                                                />
                                            </div>
                                            <div className="w-[16%]">
                                                <FormInput
                                                    name="quantity"
                                                    text=""
                                                    value={item.quantity ?? ""}
                                                    height="31px"
                                                    isViewMode={false}
                                                    isViewNoInput
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleItemUpdate(
                                                            { quantity: Number(e.target.value) || 0 },
                                                            indexItem
                                                        )
                                                    }
                                                    inputkey=""
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div className="w-[16%]">
                                                <FormInput
                                                    name="unitPrice"
                                                    text=""
                                                    isViewMode={false}
                                                    isViewNoInput
                                                    placeholder="0"
                                                    inputkey="unitPrice"
                                                    value={item.unitPrice ?? ""}
                                                    onChange={(e) =>
                                                        handleItemUpdate(
                                                            { unitPrice: Number(e.target.value) || 0 },
                                                            indexItem
                                                        )
                                                    }
                                                    height="31px"
                                                />
                                            </div>
                                            <div className="w-[19%]">
                                                <div className={cx("total-money")}>
                                                    <h3>
                                                        $
                                                        {(
                                                            (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)
                                                        ).toFixed(2)}
                                                    </h3>
                                                </div>
                                            </div>
                                            <div className="w-[5%] flex items-center">
                                                <div
                                                    className={cx("text-delete")}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => handleRemoveItem(indexItem)}
                                                    onKeyDown={() => { }}
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M3.33398 3.33398L12.6673 12.6673"
                                                            stroke="#FA6363"
                                                            strokeWidth="1.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                        <path
                                                            d="M3.33269 12.6673L12.666 3.33398"
                                                            stroke="#FA6363"
                                                            strokeWidth="1.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    <div data-slot="footer">
                        <div className={cx("section-action")}>
                            <AddItem onClick={handleAddItem} />
                        </div>
                        <div className={cx("section-item-footer")}>
                            <div className={cx("total")}>
                                <h4>Section Total:</h4>
                                <h3>${totalItems(items)}</h3>
                            </div>
                        </div>
                    </div>
                </BaseBoxContent>

                {/* Deposit */}
                <div className={cx("deposit")}>
                    <div className={cx("deposit-row")}>
                        <p className={cx("deposit-title")}>Deposit</p>
                        <div>
                            <FormToggle
                                text=""
                                inputkey=""
                                value={formData.isDeposit ?? false}
                                onChange={(e) => {
                                    handleUpdateFormData({ isDeposit: e.newValue });
                                }}
                            />
                        </div>
                    </div>
                    <p className={cx("deposit-help")}>Description</p>
                </div>

                {/* My Product Selections */}
                <div className={cx("selection-block")}>
                    <div className={cx("primary-signer")}>
                        <TitleEditor
                            value={formData.selectionsTitle ?? "My Product Selections"}
                            name=""
                            placeholder=""
                            inputkey=""
                            isViewMode={false}
                            onChange={(value) =>
                                handleUpdateFormData({ selectionsTitle: value })
                            }
                            styleView={{ fontSize: "16px", fontWeight: 600 }}
                        />
                        <h4 className={cx("primary-signer-desc")}>Description</h4>
                    </div>
                    <div className={cx("selections-list")}>
                        <DroppableArea
                            data={selections}
                            groupName="step5-selections"
                            onChange={handleDropSelections}
                            renderComponent={(item: SigningUpgradesFormData["selections"][number], index: number) => (
                                <div className={cx("production-selection")}>
                                    <div className={cx("selection-row")}>
                                        <div className={cx("drag-button")}>
                                            <DragIcon className={cx("drag-icon")} />
                                        </div>
                                        <span className={cx("selection-label")}>Selection {index + 1}</span>
                                    </div>
                                    <div className={cx("selection-fields")}>
                                        <div className={cx("selection-spacer")} />
                                        <div className={cx("selection-inputs")}>
                                            <FormInput
                                                name="item"
                                                text=""
                                                isViewMode={false}
                                                placeholder="Item"
                                                inputkey="item"
                                                value={item.fieldName ?? ""}
                                                height="56px"
                                                onChange={(e) =>
                                                    handleUpdateSelectionItem({ fieldName: e.target.value }, index)
                                                }
                                            />
                                            <FormInput
                                                name="customer"
                                                text=""
                                                isViewMode={false}
                                                placeholder="Customer Input"
                                                inputkey="customer"
                                                value={item.customerInput ?? ""}
                                                height="56px"
                                                onChange={(e) =>
                                                    handleUpdateSelectionItem({ customerInput: e.target.value }, index)
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                </div>

                {/* Footer Notes */}
                <div className={cx("footer-notes")}>
                    <FormInput
                        name="footer"
                        text="Footer"
                        placeholder=""
                        isViewMode={false}
                        inputkey="footer"
                        value={formData.footer ?? ""}
                        inputType="textarea"
                        height="121px"
                        onChange={(e) => handleUpdateFormData({ footer: e.target.value })}
                    />
                </div>
            </div>
        </div>
    );
};

export default SigningUpgrades;